import { Button } from "@/components/ui/button"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  AlertTriangle,
  Archive,
  Check,
  CheckCircle,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  UploadCloud,
} from "lucide-react"
import type { ReactNode } from "react"
import type { ComplianceEventOut } from "../../api/types/masterData"
import { CollapsibleSection } from "./CollapsibleSection"

type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

/** `memory.<verb>` → icon, checked before the generic substring rules below so a future
 *  memory verb can't accidentally fall through to a misleading one (none of the current
 *  nine collide with "created"/"import"/"updated"/"changed", but that is luck, not a rule,
 *  and this makes it not matter). */
const MEMORY_EVENT_ICONS: Record<string, ReactNode> = {
  "memory.stated": <FileText size={13} className="text-grey5" />,
  "memory.confirmed": <CheckCircle size={13} className="text-success" />,
  "memory.corrected": <Pencil size={13} className="text-grey5" />,
  "memory.superseded": <RefreshCw size={13} className="text-grey5" />,
  "memory.conflicted": <AlertTriangle size={13} className="text-attention" />,
  "memory.resolved": <Check size={13} className="text-success" />,
  "memory.accepted": <ThumbsUp size={13} className="text-grey5" />,
  "memory.reverted": <ThumbsDown size={13} className="text-grey5" />,
  "memory.retired": <Archive size={13} className="text-grey5" />,
}

function getEventIcon(event: ComplianceEventOut): ReactNode {
  const t = event.eventType.toLowerCase()
  if (t in MEMORY_EVENT_ICONS) return MEMORY_EVENT_ICONS[t]
  if (t === "declaration_approved") {
    return <CheckCircle size={13} className="text-success" />
  }
  if (t.includes("created")) {
    return <Sparkles size={13} className="text-grey5" />
  }
  if (t.includes("import") || event.sourceImportFilename != null) {
    return <UploadCloud size={13} className="text-grey5" />
  }
  if (t.includes(":") || t.includes("updated") || t.includes("changed")) {
    return <Pencil size={13} className="text-grey5" />
  }
  return <FileText size={13} className="text-grey5" />
}

/** Convert a snake_case field name to a human label — fallback for field keys
 * not in FIELD_LABEL_KEYS (e.g. rare/technical columns no diff has surfaced
 * yet). Not translated: it's a readability fallback, not UI copy. */
function humanizeFieldName(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

// Master-data column names that appear as diff keys on field_updated /
// import_field_updated / compliance_overridden / substance_updated events —
// mirrors backend/turnus_ai/database/mc_models.py (mc_articles,
// mc_article_compliance, mc_article_substances, mc_suppliers, mc_products,
// mc_customers, mc_substances). Reuses the same labels the mapping screen and
// entity field forms already show for these columns. Fields not listed here
// (mostly internal/system columns like engine_version, chain_depth,
// framework_statuses) fall back to humanizeFieldName above.
const FIELD_LABEL_KEYS: Record<string, TranslationKeys> = {
  article_number: "MC_MD_MAP_FIELD_ARTICLES_ARTICLE_NUMBER",
  name: "MC_MD_FIELD_NAME",
  supplier_id: "MC_MD_PANEL_COL_SUPPLIER",
  manufacturer_id: "MC_MD_MAP_FIELD_ARTICLES_MANUFACTURER_ID",
  manufacturer_name: "MC_MD_MAP_FIELD_ARTICLES_MANUFACTURER_NAME",
  country_of_origin: "MC_MD_FIELD_COUNTRY_OF_ORIGIN",
  material_group: "MC_MD_FIELD_MATERIAL_GROUP",
  product_group: "MC_MD_FIELD_PRODUCT_GROUP",
  scip_number: "MC_MD_MAP_FIELD_ARTICLES_SCIP_NUMBER",
  status: "MC_MD_PANEL_COL_STATUS",
  regulation_id: "MC_MD_PANEL_COL_REGULATION",
  exemption_code: "MC_MD_MAP_FIELD_COMPLIANCE_EXEMPTION_CODE",
  valid_until: "MC_MD_FIELD_VALID_UNTIL",
  confirmed_at: "MC_MD_FIELD_CONFIRMED_AT",
  concentration_pct: "MC_MD_MAP_FIELD_SUBSTANCE_DATA_CONCENTRATION_PCT",
  source: "MC_MD_PANEL_COL_SOURCE",
  supplier_number: "MC_MD_FIELD_SUPPLIER_NUMBER",
  email: "MC_MD_FIELD_EMAIL",
  country: "MC_MD_FIELD_COUNTRY",
  duns_number: "MC_MD_FIELD_DUNS",
  product_number: "MC_MD_FIELD_PRODUCT_NUMBER",
  revision: "MC_MD_FIELD_REVISION",
  customer_number: "MC_MD_FIELD_CUSTOMER_NUMBER",
  cas_number: "MC_MD_MAP_FIELD_SUBSTANCES_CAS_NUMBER",
  ec_number: "MC_MD_FIELD_EC_NUMBER",
  svhc_listed: "MC_MD_FIELD_SVHC",
  svhc_listed_since: "MC_MD_FIELD_SVHC_LISTED_SINCE",
  echa_list_version: "MC_MD_FIELD_ECHA_LIST_VERSION",
  origin: "MC_MD_FIELD_ORIGIN",
}

/** Translated label for a diff field name; falls back to a humanized raw key
 * for fields outside FIELD_LABEL_KEYS. */
/** The API interceptor camelCases `newValue` keys (articleNumber), while the
 *  map above is keyed like the backend columns (article_number). Normalize
 *  before the lookup so both shapes resolve to the same label. */
function toSnakeCase(key: string): string {
  return key.replace(/([A-Z])/g, (c) => `_${c.toLowerCase()}`)
}

/** Payload keys that describe the event rather than a changed field. The
 *  backend writes `bulk_count` on every per-article event of a bulk action;
 *  the interceptor camelCases it. */
const META_KEYS = new Set(["bulk_count", "bulkCount"])

function bulkCountOf(event: ComplianceEventOut): number | null {
  const nv = event.newValue as Record<string, unknown> | null
  const raw = nv?.bulkCount ?? nv?.bulk_count
  return typeof raw === "number" ? raw : null
}

/** Per-field label overrides, keyed like the backend columns. Used for columns
 *  an org renames for itself (today: product_group), so the audit line says
 *  what the rest of its master data says. */
export type FieldLabelOverrides = Record<string, string | undefined>

function fieldLabel(
  key: string,
  translate: TranslateFn,
  overrides?: FieldLabelOverrides,
): string {
  const snake = toSnakeCase(key)
  const override = overrides?.[snake]
  if (override) return override
  const labelKey = FIELD_LABEL_KEYS[snake]
  return labelKey ? translate(labelKey) : humanizeFieldName(key)
}

/** Pretty-print a JSON value for the audit diff. */
function formatValue(v: unknown): string {
  if (v == null || v === "") return "—"
  if (typeof v === "object") return JSON.stringify(v)
  return String(v)
}

/**
 * Build a human-readable label from an event_type + value payload.
 * For *_field_updated events the changed field names are appended.
 */
function prettyEventLabel(
  event: ComplianceEventOut,
  translate: TranslateFn,
  overrides?: FieldLabelOverrides,
): string {
  const t = event.eventType
  const fieldKeys =
    event.newValue && typeof event.newValue === "object"
      ? Object.keys(event.newValue).filter((k) => !META_KEYS.has(k))
      : []
  const fieldList = fieldKeys
    .map((k) => fieldLabel(k, translate, overrides))
    .join(", ")
  const bulkCount = bulkCountOf(event)

  switch (t) {
    case "field_updated":
      if (bulkCount != null && fieldList)
        return translate("MC_MD_AUDIT_EVT_FIELD_UPDATED_BULK", {
          fields: fieldList,
          n: bulkCount,
        })
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_FIELD_UPDATED_LIST", { fields: fieldList })
        : translate("MC_MD_AUDIT_EVT_FIELD_UPDATED")
    case "import_field_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_IMPORT_FIELD_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_IMPORT_FIELD_UPDATED")
    case "created":
      return translate("MC_MD_AUDIT_EVT_CREATED")
    case "imported":
      return translate("MC_MD_AUDIT_EVT_IMPORTED")
    case "uploaded":
      return translate("MC_MD_AUDIT_EVT_UPLOADED")
    case "discarded":
      return translate("MC_MD_AUDIT_EVT_DISCARDED")
    case "sheet_skipped":
      return translate("MC_MD_AUDIT_EVT_SHEET_SKIPPED")
    case "ai_detected":
      return translate("MC_MD_AUDIT_EVT_AI_DETECTED")
    case "compliance_status_changed":
      return translate("MC_MD_AUDIT_EVT_COMPLIANCE_STATUS_CHANGED")
    case "declaration_approved":
      return translate("MC_MD_AUDIT_EVT_DECL_APPROVED")
    case "declaration_received":
      return translate("MC_MD_AUDIT_EVT_DECL_RECEIVED")
    case "declaration_processed":
      return translate("MC_MD_AUDIT_EVT_DECL_PROCESSED")
    case "declaration_processing_failed":
      return translate("MC_MD_AUDIT_EVT_DECL_PROCESSING_FAILED")
    case "declaration_roundtrip_held":
      return translate("MC_MD_AUDIT_EVT_DECL_ROUNDTRIP_HELD")
    case "declaration_extraction_held":
      return translate("MC_MD_AUDIT_EVT_DECL_EXTRACTION_HELD")
    case "declaration_hold_released":
      return translate("MC_MD_AUDIT_EVT_DECL_HOLD_RELEASED")
    case "declaration_codes_pending_resolution":
      return translate("MC_MD_AUDIT_EVT_DECL_CODES_PENDING")
    case "declaration_mappings_resolved":
      return translate("MC_MD_AUDIT_EVT_DECL_MAPPINGS_RESOLVED")
    case "declaration_reprocess_requested":
      return translate("MC_MD_AUDIT_EVT_DECL_REPROCESS_REQUESTED")
    case "question_answered":
      return translate("MC_MD_AUDIT_EVT_QUESTION_ANSWERED")
    case "claim_accepted":
      return translate("MC_MD_AUDIT_EVT_CLAIM_ACCEPTED")
    case "claim_rejected":
      return translate("MC_MD_AUDIT_EVT_CLAIM_REJECTED")
    case "claim_superseded":
      return translate("MC_MD_AUDIT_EVT_CLAIM_SUPERSEDED")
    case "claim_conflicting":
      return translate("MC_MD_AUDIT_EVT_CLAIM_CONFLICTING")
    case "statement_accepted":
      return translate("MC_MD_AUDIT_EVT_STATEMENT_ACCEPTED")
    case "statement_rejected":
      return translate("MC_MD_AUDIT_EVT_STATEMENT_REJECTED")
    case "supplier_link_added":
      return translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_ADDED")
    case "supplier_link_removed":
      return translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_REMOVED")
    case "supplier_link_activated":
      return translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_ACTIVATED")
    case "supplier_link_deactivated":
      return translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_DEACTIVATED")
    case "supplier_link_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_SUPPLIER_LINK_UPDATED")
    case "import_supplier_link_added":
      return translate("MC_MD_AUDIT_EVT_IMPORT_SUPPLIER_LINK_ADDED")
    case "import_supplier_link_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_IMPORT_SUPPLIER_LINK_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_IMPORT_SUPPLIER_LINK_UPDATED")
    case "import_substance_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_IMPORT_SUBSTANCE_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_IMPORT_SUBSTANCE_UPDATED")
    case "import_compliance_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_IMPORT_COMPLIANCE_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_IMPORT_COMPLIANCE_UPDATED")
    case "import_supplier_mapping_updated":
      return translate("MC_MD_AUDIT_EVT_IMPORT_SUPPLIER_MAPPING_UPDATED")
    case "import_product_article_updated":
      return translate("MC_MD_AUDIT_EVT_IMPORT_PRODUCT_ARTICLE_UPDATED")
    case "import_bom_updated":
      return translate("MC_MD_AUDIT_EVT_IMPORT_BOM_UPDATED")
    case "compliance_overridden":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_COMPLIANCE_OVERRIDDEN_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_COMPLIANCE_OVERRIDDEN")
    case "substance_updated":
      return fieldList
        ? translate("MC_MD_AUDIT_EVT_SUBSTANCE_UPDATED_LIST", {
            fields: fieldList,
          })
        : translate("MC_MD_AUDIT_EVT_SUBSTANCE_UPDATED")
    case "substance_created":
      return translate("MC_MD_AUDIT_EVT_SUBSTANCE_CREATED")
    case "substance_link_added":
      return translate("MC_MD_AUDIT_EVT_SUBSTANCE_LINK_ADDED")
    case "substance_link_removed":
      return translate("MC_MD_AUDIT_EVT_SUBSTANCE_LINK_REMOVED")
    case "article_link_added":
      return translate("MC_MD_AUDIT_EVT_ARTICLE_LINK_ADDED")
    case "article_link_updated":
      return translate("MC_MD_AUDIT_EVT_ARTICLE_LINK_UPDATED")
    case "article_link_removed":
      return translate("MC_MD_AUDIT_EVT_ARTICLE_LINK_REMOVED")
    // `memory.<verb>` — the memory lineage trail's own vocabulary, namespaced so it can
    // never collide with a future master-data event type. Each is its own case rather
    // than a shared one, deliberately: superseded vs retired vs conflicted are exactly
    // the distinctions this trail exists to show, and collapsing them would be the one
    // way C2 could still ship broken after the switch itself stopped erroring.
    case "memory.stated":
      return translate("MC_MD_AUDIT_EVT_MEMORY_STATED")
    case "memory.confirmed":
      return translate("MC_MD_AUDIT_EVT_MEMORY_CONFIRMED")
    case "memory.corrected":
      return translate("MC_MD_AUDIT_EVT_MEMORY_CORRECTED")
    case "memory.superseded":
      return translate("MC_MD_AUDIT_EVT_MEMORY_SUPERSEDED")
    case "memory.conflicted":
      return translate("MC_MD_AUDIT_EVT_MEMORY_CONFLICTED")
    case "memory.resolved":
      return translate("MC_MD_AUDIT_EVT_MEMORY_RESOLVED")
    case "memory.accepted":
      return translate("MC_MD_AUDIT_EVT_MEMORY_ACCEPTED")
    case "memory.reverted":
      return translate("MC_MD_AUDIT_EVT_MEMORY_REVERTED")
    case "memory.retired":
      return translate("MC_MD_AUDIT_EVT_MEMORY_RETIRED")
    default:
      // Unknown event type — arbitrary backend data, not a fixed UI string,
      // so it can't be keyed per-value. The raw type is still shown (as a
      // parameter of a translated sentence) rather than rendered untranslated.
      return translate("MC_MD_AUDIT_EVT_UNKNOWN", { type: t })
  }
}

/** Render a compact `field: old → new` diff for field-update events.
 *
 *  A `memory.<verb>` EVENT IS NOT A FIELD UPDATE and never reaches the diff below: it carries
 *  one thing worth showing, the value the entry said (`newValue.value`, already rendered by
 *  the backend), so it gets its own one-line render instead of a master-data shape. */
function renderDiff(
  event: ComplianceEventOut,
  translate: TranslateFn,
  overrides?: FieldLabelOverrides,
): ReactNode {
  if (event.eventType.startsWith("memory.")) {
    const said = event.newValue?.value
    if (typeof said !== "string" || said === "") return null
    return (
      <p
        className="font-mono mt-1 text-xs text-white"
        data-testid="audit-memory-value"
      >
        {said}
      </p>
    )
  }
  const isFieldUpdate =
    event.eventType === "field_updated" ||
    event.eventType === "import_field_updated" ||
    event.eventType === "compliance_overridden" ||
    event.eventType === "substance_updated"
  if (!isFieldUpdate) return null

  const newVal: Record<string, unknown> = event.newValue ?? {}
  const oldVal: Record<string, unknown> = event.oldValue ?? {}
  const keys = Object.keys(newVal).filter(
    (k) => k !== "link_id" && !META_KEYS.has(k),
  )
  if (keys.length === 0) return null

  return (
    <ul className="space-y-0.5 mt-1 text-xs text-grey6">
      {keys.map((k) => (
        <li key={k} className="font-mono">
          <span className="text-grey5">
            {fieldLabel(k, translate, overrides)}:
          </span>{" "}
          <span className="text-grey6 line-through">
            {formatValue(oldVal[k])}
          </span>{" "}
          <span className="text-grey5">→</span>{" "}
          <span className="text-white">{formatValue(newVal[k])}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Render a rich inline summary for `declaration_approved` events.
 *
 * Handles two `newValue` shapes (keys are camelCased by the API interceptor —
 * the backend stores them snake_case):
 *   - Structured shape: { summary: { supplierName, headline (not rendered, see below), verdictCount, substanceCount }, source: { ... } }
 *   - Legacy flat shape (older records): { supplierName, statementWrites, substanceWrites, regulationVerdicts }
 *
 * Falls back to null (title-only render) when the payload has neither shape.
 */
function renderDeclarationApproved(
  event: ComplianceEventOut,
  translate: TranslateFn,
): ReactNode {
  if (event.eventType !== "declaration_approved") return null

  const nv = event.newValue as Record<string, unknown> | null
  if (!nv) return null

  // New shape: newValue.summary.*
  const summary = nv.summary as Record<string, unknown> | undefined
  if (summary) {
    const supplier = summary.supplierName as string | undefined
    const verdictCount = summary.verdictCount as number | undefined
    const substanceCount = summary.substanceCount as number | undefined
    // summary.headline (deliberately not rendered): the backend computes it
    // as either a single framework name (a proper noun, e.g. "RoHS") or the
    // literal English string 'for multiple frameworks'
    // (mc_declaration_review_service.py::_fw_headline) — an untranslatable
    // mix, so it's dropped and the line falls back to supplier + counts.

    const parts: string[] = []
    if (supplier) parts.push(supplier)
    const counts: string[] = []
    if (verdictCount != null)
      counts.push(
        translate("MC_MD_AUDIT_VERDICT_COUNT", { count: verdictCount }),
      )
    if (substanceCount != null)
      counts.push(
        translate("MC_MD_AUDIT_SUBSTANCE_COUNT", { count: substanceCount }),
      )
    if (counts.length) parts.push(counts.join(", "))

    if (parts.length === 0) return null

    return <p className="mt-0.5 text-xs text-grey5">{parts.join(" · ")}</p>
  }

  // Legacy flat shape (camelCased): newValue.supplierName / statementWrites / substanceWrites
  const supplierName = nv.supplierName as string | undefined
  const statementWrites = nv.statementWrites as number | undefined
  const substanceWrites = nv.substanceWrites as number | undefined

  if (!supplierName && statementWrites == null && substanceWrites == null)
    return null

  const parts: string[] = []
  if (supplierName) parts.push(supplierName)
  const counts: string[] = []
  if (statementWrites != null)
    counts.push(
      translate("MC_MD_AUDIT_VERDICT_COUNT", { count: statementWrites }),
    )
  if (substanceWrites != null)
    counts.push(
      translate("MC_MD_AUDIT_SUBSTANCE_COUNT", { count: substanceWrites }),
    )
  if (counts.length) parts.push(counts.join(", "))

  if (parts.length === 0) return null

  return <p className="mt-0.5 text-xs text-grey5">{parts.join(" · ")}</p>
}

/**
 * Format a date string as a human-readable date + time in the given locale
 * (the selected app language), e.g. `19.05.2026, 14:08` for `de`.
 */
function formatDate(d: string, locale: string): string {
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return d
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

interface AuditTrailProps {
  events: ComplianceEventOut[]
  isLoading?: boolean
  hasMore?: boolean
  isFetchingMore?: boolean
  onLoadMore?: () => unknown
  /** Renamed field labels for this entity's diffs. Only the article panel passes
   *  one (the org's own word for product_group); every other panel keeps the
   *  built-in labels, so no extra settings fetch happens there. */
  fieldLabels?: FieldLabelOverrides
  /** Starts the section expanded. Every existing caller renders this collapsed inside a
   *  detail panel already showing other sections, so the default stays `false` — only
   *  `MemoryLineageSheet` passes `true`, where the surrounding sheet IS the "show me this"
   *  gesture and a second collapsed layer on top of it would be asking twice. */
  defaultOpen?: boolean
}

/**
 * Renders audit events as a single bordered card. Shared by suppliers, articles,
 * products, customers, substances. When `hasMore` is true, a "Load more" button
 * is rendered below the list and calls `onLoadMore`.
 */
export function AuditTrail({
  events,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  fieldLabels,
  defaultOpen = false,
}: AuditTrailProps) {
  // Render timestamps in the selected app language (EN/DE/JA → en/de/ja).
  const { translate, currentLanguage } = useTranslation()
  const locale = currentLanguage.toLowerCase()
  if (isLoading) {
    return (
      <CollapsibleSection
        label={translate("MC_MD_AUDIT_SECTION")}
        defaultOpen={defaultOpen}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 border-b border-grey4 px-4 py-3 last:border-b-0"
          >
            <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-grey4" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3 w-2/3 animate-pulse rounded bg-grey4" />
              <div className="h-3 w-1/3 animate-pulse rounded bg-grey4" />
            </div>
          </div>
        ))}
      </CollapsibleSection>
    )
  }

  if (events.length === 0) {
    return (
      <CollapsibleSection
        label={translate("MC_MD_AUDIT_SECTION")}
        defaultOpen={defaultOpen}
      >
        <p className="p-4 text-xs text-grey5">
          {translate("MC_MD_AUDIT_EMPTY")}
        </p>
      </CollapsibleSection>
    )
  }

  return (
    <CollapsibleSection
      label={translate("MC_MD_AUDIT_SECTION")}
      defaultOpen={defaultOpen}
      badge={<span className="text-xs text-grey5">{events.length}</span>}
    >
      <>
        {events.map((event) => (
          <div
            key={event.id}
            className="flex items-start gap-3 border-b border-grey4 px-4 py-3 last:border-b-0"
          >
            {/* Icon */}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-grey4 bg-grey3">
              {getEventIcon(event)}
            </div>
            {/* Content */}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white">
                {prettyEventLabel(event, translate, fieldLabels)}
              </p>
              <p className="mt-0.5 text-xs text-grey5">
                <span className="font-mono">
                  {formatDate(event.performedAt, locale)}
                </span>
                {event.sourceImportFilename != null && (
                  <>
                    {" · "}
                    {event.sourceImportUrl != null ? (
                      <a
                        href={event.sourceImportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-grey6 underline hover:text-white"
                      >
                        {translate("MC_MD_AUDIT_VIA", {
                          name: event.sourceImportFilename,
                        })}
                        <ExternalLink size={10} />
                      </a>
                    ) : (
                      <span className="text-grey6">
                        {translate("MC_MD_AUDIT_VIA", {
                          name: event.sourceImportFilename,
                        })}
                      </span>
                    )}
                  </>
                )}
                {event.sourceDocName != null && (
                  <>
                    {" · "}
                    {event.sourceDocUrl != null ? (
                      <a
                        href={event.sourceDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-grey6 underline hover:text-white"
                      >
                        {event.sourceDocName}
                        <ExternalLink size={10} />
                      </a>
                    ) : (
                      <span className="text-grey6">{event.sourceDocName}</span>
                    )}
                  </>
                )}
              </p>
              {renderDeclarationApproved(event, translate)}
              {renderDiff(event, translate, fieldLabels)}
            </div>
          </div>
        ))}
      </>

      {hasMore && (
        <div className="flex justify-center py-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-grey5 hover:text-white"
            disabled={isFetchingMore}
            onClick={() => onLoadMore?.()}
          >
            {isFetchingMore ? (
              <>
                <Loader2 size={12} className="mr-1.5 animate-spin" />
                {translate("MC_SUBTABLE_LOADING")}
              </>
            ) : (
              translate("MC_MD_LIST_LOAD_MORE")
            )}
          </Button>
        </div>
      )}
    </CollapsibleSection>
  )
}
